import { useState, useRef, useEffect } from "react"
import { caretDown } from "../../assets/images"
import "./Dropdown.scss"

interface DropdownProps {
  id: string
  name?: string
  onChange: (value: string) => void
  value: string
  options: { value: string; label: string }[]
  labelText?: string
  placeholder?: string
}

export const Dropdown: React.FC<DropdownProps> = ({
  id,
  name,
  onChange,
  value,
  options,
  labelText,
  placeholder,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const selectedOption = options.find((opt) => opt.value === value)
  const displayText = selectedOption?.label || placeholder || ""

  const handleSelect = (optionValue: string) => {
    onChange(optionValue)
    setIsOpen(false)
  }

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault()
      setIsOpen(!isOpen)
    } else if (event.key === "Escape") {
      setIsOpen(false)
    } else if (event.key === "ArrowDown" && isOpen) {
      event.preventDefault()
      const currentIndex = options.findIndex((opt) => opt.value === value)
      const nextIndex = currentIndex < options.length - 1 ? currentIndex + 1 : 0
      onChange(options[nextIndex].value)
    } else if (event.key === "ArrowUp" && isOpen) {
      event.preventDefault()
      const currentIndex = options.findIndex((opt) => opt.value === value)
      const prevIndex = currentIndex > 0 ? currentIndex - 1 : options.length - 1
      onChange(options[prevIndex].value)
    }
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  return (
    <div className="dropdown-container" ref={containerRef}>
      {labelText && (
        <label htmlFor={id} className="label">
          {labelText}
        </label>
      )}
      {name && <input type="hidden" name={name} value={value} />}
      <button
        id={id}
        type="button"
        className={`dropdown-trigger ${isOpen ? "open" : ""} ${
          !selectedOption ? "placeholder" : ""
        }`}
        onClick={() => setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className="dropdown-text">{displayText}</span>
        <img src={caretDown} alt="" className="dropdown-caret" />
      </button>
      {isOpen && (
        <ul className="dropdown-options" role="listbox">
          {options
            .filter((option) => option.value !== selectedOption?.value)
            .map((option) => (
              <li
                key={option.value}
                className={`dropdown-option ${
                  option.value === value ? "selected" : ""
                }`}
                onClick={() => handleSelect(option.value)}
                role="option"
                aria-selected={option.value === value}
              >
                {option.label}
              </li>
            ))}
        </ul>
      )}
    </div>
  )
}
